import {createApp} from "vue";
import App from "./App.vue";
import "@/assets/font/GoogleSans.scss";
import "@/styles/app.scss";
import {router} from "./router";
import Nav from "./layout/nav/NavLayout.vue";
import ModalBlock from "./layout/nav/components/modal/ModalBlock.vue";
import ButtonSave from "@/elements/buttonSave/ButtonSave.vue";
import copyButton from "@/elements/copyButton/CopyButton.vue";
import Default from "./layout/Default.vue";
import {imaskDirective} from "@/directives/imask";
import PhoneInput from "./elements/phoneInput/PhoneInput.vue";
import buttonCross from "./elements/buttonCross/buttonCross.vue";
import {getErr} from "./scripts/getErr.js";
import {autoInitPhotoLoader} from "./scripts/general.js";
import {installApiImgFallback} from "./scripts/apiImgFallback";
import {createPinia} from "pinia";
import {useQuizStore} from "@/stores/quizData";
import {useWidgetStore} from "@/stores/WidgetState";
import {useIntegrationStore} from "@/stores/IntegrationData";
import {useUserStore} from "@/stores/UserData";
import {useClinicStore} from "@/stores/ClinicData";
import {useDoctorsStore} from "@/stores/DoctorsData";
import {usePanelStore} from "@/stores/PanelData";

const pinia = createPinia();
const app = createApp(App).use(router).use(pinia);
const dynamicImportReloadKey = "readds:dynamic-import-reload";

function shouldReloadAfterDynamicImportError(error: unknown): boolean {
  const message = error instanceof Error ? error.message.toLowerCase() : "";
  return (
    message.includes("failed to fetch dynamically imported module") ||
    message.includes("error loading dynamically imported module") ||
    message.includes("importing a module script failed")
  );
}

function reloadAfterDynamicImportError(): boolean {
  try {
    const lastReloadAt = Number(
      window.sessionStorage.getItem(dynamicImportReloadKey) || 0
    );
    if (Date.now() - lastReloadAt < 60_000) {
      return false;
    }
    window.sessionStorage.setItem(dynamicImportReloadKey, String(Date.now()));
    window.location.reload();
    return true;
  } catch {
    return false;
  }
}

installApiImgFallback();
app.directive("imask", imaskDirective);
Date.prototype.getWeekOfMonth = function (): number {
  const month = this.getMonth();
  const year = this.getFullYear();
  const firstWeekday = new Date(year, month, 1).getDay();
  const offsetDate = this.getDate() + firstWeekday - 1;
  const week = Math.floor(offsetDate / 7);
  return week;
};

app.component("navLayout", Nav);
app.component("ModalBlock", ModalBlock);
app.component("ButtonSave", ButtonSave);
app.component("PhoneInput", PhoneInput);
app.component("copyButton", copyButton);
app.component("defaultLayout", Default);
app.component("buttonCross", buttonCross);

app.config.globalProperties.$quizStore = useQuizStore(pinia);
app.config.globalProperties.$widgetStore = useWidgetStore(pinia);
app.config.globalProperties.$integrationStore = useIntegrationStore(pinia);
app.config.globalProperties.$userStore = useUserStore(pinia);
app.config.globalProperties.$clinicStore = useClinicStore(pinia);
app.config.globalProperties.$doctorStore = useDoctorsStore(pinia);
app.config.globalProperties.$panelStore = usePanelStore(pinia);

if (!window.location.host.includes("localhost")) {
  app.config.errorHandler = (err, instance, info): void => {
    if (
      shouldReloadAfterDynamicImportError(err) &&
      reloadAfterDynamicImportError()
    ) {
      return;
    }

    void getErr(err, instance, info);
  };
}

app.mount("#app");
autoInitPhotoLoader();
